/** IVR flow graphs as the API stores them (see apps/api/src/ivr/flow.ts). */
import type { IconName } from '../components/ui';
import { getToken } from './api';

export type NodeType = 'start' | 'play' | 'menu' | 'collect' | 'hours' | 'http' | 'queue' | 'voicemail' | 'transfer' | 'hangup';
export type PromptRef = { kind: 'prompt'; id: string } | { kind: 'sound'; name: string };

export interface FlowNode {
  id: string;
  type: NodeType;
  x: number;
  y: number;
  label?: string;
  data: Record<string, unknown>;
}
export interface FlowEdge {
  from: string;
  port: string;
  to: string;
}
export interface Graph {
  nodes: FlowNode[];
  edges: FlowEdge[];
}
export interface Problem {
  severity: 'error' | 'warning';
  nodeId?: string;
  message: string;
}
export interface FlowVersion {
  id: string;
  version: number;
  published_at: string;
  published_by: string | null;
  live: boolean;
}
export interface FlowDetail {
  id: string;
  name: string;
  draft: Graph;
  draft_updated_at: string;
  published_version_id: string | null;
  versions: FlowVersion[];
  numbers: string[];
  problems: Problem[];
}
export interface Prompt {
  id: string;
  name: string;
  language: 'en' | 'sw' | 'fr';
  duration_ms: number;
  created_at: string;
  used_in: string[];
}

export const LANGUAGES: Record<Prompt['language'], string> = { en: 'English', sw: 'Swahili', fr: 'French' };

export const STEPS: Record<NodeType, { name: string; hint: string; icon: IconName; tint: string }> = {
  start: { name: 'Start', hint: 'Where every call begins', icon: 'phone', tint: 'bg-ion/15 text-ion' },
  play: { name: 'Play message', hint: 'Play a recording, then continue', icon: 'play', tint: 'bg-ion/15 text-ion' },
  menu: { name: 'Menu', hint: '"Press 1 for sales…"', icon: 'grid', tint: 'bg-oncall/15 text-oncall' },
  collect: { name: 'Collect digits', hint: 'Ask for an account or ID number', icon: 'keypad', tint: 'bg-oncall/15 text-oncall' },
  hours: { name: 'Business hours', hint: 'Branch on open or closed', icon: 'history', tint: 'bg-wrapup/15 text-wrapup' },
  http: { name: 'HTTP lookup', hint: 'Ask your CRM or billing system, branch on the reply', icon: 'search', tint: 'bg-wrapup/15 text-wrapup' },
  queue: { name: 'Queue', hint: 'Hand the caller to agents', icon: 'headset', tint: 'bg-available/15 text-available' },
  voicemail: { name: 'Voicemail', hint: 'Take a message', icon: 'rec', tint: 'bg-available/15 text-available' },
  transfer: { name: 'Transfer', hint: 'Ring an outside number', icon: 'transfer', tint: 'bg-available/15 text-available' },
  hangup: { name: 'Hang up', hint: 'Say goodbye and end the call', icon: 'hangup', tint: 'bg-break/15 text-break' },
};

/** Settings a newly added step starts with. */
export const DEFAULTS: Record<NodeType, Record<string, unknown>> = {
  start: {},
  play: {},
  menu: { options: ['1', '2'], timeoutSeconds: 5, attempts: 3 },
  collect: { field: 'account', maxDigits: 10, timeoutSeconds: 5, terminator: '#' },
  hours: {},
  http: { method: 'GET', timeoutSeconds: 4, resultPath: 'status', values: ['active'], keep: [] },
  queue: { playGreeting: false },
  voicemail: { maxSeconds: 120 },
  transfer: { ringSeconds: 30 },
  hangup: { prompt: { kind: 'sound', name: 'vm-goodbye' } },
};

export function outputsOf(type: NodeType, data: Record<string, unknown>): string[] {
  switch (type) {
    case 'start':
    case 'play':
      return ['next'];
    case 'menu':
      return [...(Array.isArray(data.options) ? (data.options as string[]) : []), 'noinput'];
    case 'collect':
      return ['next', 'noinput'];
    case 'hours':
      return ['open', 'closed'];
    case 'transfer':
      return ['failed'];
    case 'http':
      return [...(Array.isArray(data.values) ? (data.values as string[]).map((v) => `=${v}`) : []), 'other', 'failed'];
    default:
      return [];
  }
}

export function portName(port: string, type?: NodeType): string {
  if (port.startsWith('=')) return `= ${port.slice(1)}`;
  if (port === 'failed') return type === 'http' ? 'Failed' : 'No answer';
  return ({ next: 'Next', noinput: 'No input', open: 'Open', closed: 'Closed', other: 'Other' } as Record<string, string>)[port] ?? `Key ${port}`;
}

/** Asterisk sounds every server has, offered next to the organization's own prompts. */
export const BUILT_IN_SOUNDS: { name: string; text: string }[] = [
  { name: 'hello-world', text: 'Hello world' },
  { name: 'one-moment-please', text: 'One moment please' },
  { name: 'pls-hold-while-try', text: 'Please hold while I try that extension' },
  { name: 'please-try-again', text: 'Please try again' },
  { name: 'pm-invalid-option', text: 'That is not a valid option' },
  { name: 'im-sorry', text: "I'm sorry" },
  { name: 'please-try-call-later', text: 'Please try your call again later' },
  { name: 'vm-intro', text: 'Please leave your message after the tone' },
  { name: 'vm-goodbye', text: 'Goodbye' },
];

export function promptLabel(ref: PromptRef | undefined, prompts: Prompt[]): string | null {
  if (!ref) return null;
  if (ref.kind === 'sound') return `"${BUILT_IN_SOUNDS.find((s) => s.name === ref.name)?.text ?? ref.name}"`;
  const p = prompts.find((x) => x.id === ref.id);
  return p ? `${p.name} (${LANGUAGES[p.language]})` : 'Deleted message';
}

/**
 * Converts any audio file the browser can play into what Asterisk plays:
 * an 8 kHz, 16-bit, mono WAV. Quiet recordings are raised to a normal level.
 */
export async function toPhoneWav(file: Blob): Promise<{ wav: Blob; seconds: number }> {
  const ctx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(await file.arrayBuffer());
  } catch {
    throw new Error('This file is not audio the browser can read. Try MP3 or WAV.');
  } finally {
    void ctx.close();
  }
  const frames = Math.max(1, Math.ceil(decoded.duration * 8000));
  const offline = new OfflineAudioContext(1, frames, 8000);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const samples = (await offline.startRendering()).getChannelData(0);

  let peak = 0;
  for (const s of samples) peak = Math.max(peak, Math.abs(s));
  const gain = peak > 0 && peak < 0.5 ? 0.9 / peak : 1;
  const pcm = new DataView(new ArrayBuffer(44 + samples.length * 2));
  const ascii = (at: number, s: string) => [...s].forEach((c, i) => pcm.setUint8(at + i, c.charCodeAt(0)));
  ascii(0, 'RIFF');
  pcm.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  pcm.setUint32(16, 16, true);
  pcm.setUint16(20, 1, true);
  pcm.setUint16(22, 1, true);
  pcm.setUint32(24, 8000, true);
  pcm.setUint32(28, 16000, true);
  pcm.setUint16(32, 2, true);
  pcm.setUint16(34, 16, true);
  ascii(36, 'data');
  pcm.setUint32(40, samples.length * 2, true);
  samples.forEach((s, i) => pcm.setInt16(44 + i * 2, Math.max(-1, Math.min(1, s * gain)) * 0x7fff, true));
  return { wav: new Blob([pcm.buffer], { type: 'audio/wav' }), seconds: samples.length / 8000 };
}

let player: { id: string; audio: HTMLAudioElement; url: string } | null = null;

/** Plays a library prompt in the browser; calling it again for the same prompt stops it. */
export async function previewPrompt(id: string, onEnd?: () => void): Promise<boolean> {
  const same = player?.id === id;
  stopPreview();
  if (same) return false;
  const res = await fetch(`/api/v1/prompts/${id}/audio`, { headers: { Authorization: `Bearer ${getToken() ?? ''}` } });
  if (!res.ok) throw new Error('Could not load the message');
  const url = URL.createObjectURL(await res.blob());
  const audio = new Audio(url);
  player = { id, audio, url };
  audio.onended = () => {
    stopPreview();
    onEnd?.();
  };
  await audio.play();
  return true;
}

export function stopPreview(): void {
  if (!player) return;
  player.audio.pause();
  URL.revokeObjectURL(player.url);
  player = null;
}
